import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  MultiSelect,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconBriefcase,
  IconFileCheck,
  IconMapPin,
  IconRuler2,
  IconUser,
} from "@/lib/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { createPatient, type PatientCreatePayload, type Sex } from "@/lib/api/patients";
import {
  createExamination,
  type DoctorSpecialty,
  type ExaminationCreatePayload,
  type ExaminationType,
} from "@/lib/api/examinations";
import { ApiError } from "@/lib/api/client";

const EXAM_TYPES: ExaminationType[] = [
  "initial",
  "periodic",
  "form_086",
  "form_083",
  "form_089",
];

const ALL_SPECIALTIES: DoctorSpecialty[] = [
  "therapist",
  "neurologist",
  "ophthalmologist",
  "ent",
  "surgeon",
  "gynecologist",
  "urologist",
  "dentist",
  "psychiatrist_narcologist",
  "dermatovenerologist",
  "cardiologist",
  "radiologist",
  "laboratory",
];

const FEMALE_ONLY: DoctorSpecialty[] = ["gynecologist"];
const MALE_ONLY: DoctorSpecialty[] = ["urologist"];

function specialtiesForSex(sex: Sex | "" | null): DoctorSpecialty[] {
  if (sex === "female") return ALL_SPECIALTIES.filter((s) => !MALE_ONLY.includes(s));
  if (sex === "male") return ALL_SPECIALTIES.filter((s) => !FEMALE_ONLY.includes(s));
  return ALL_SPECIALTIES;
}
import {
  canonicalPhoneUz,
  formatPassportUz,
  formatPhoneUz,
  passportParts,
} from "@/lib/format";

export function PatientFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: {
      full_name_raw: "",
      birth_date: null as Date | null,
      sex: "" as Sex | "",
      jshshir: "",
      passport_raw: "",
      address_region: "",
      address_district: "",
      address_full: "",
      phone_raw: "",
      email: "",
      workplace: "",
      position: "",
      consent_signed: false,
      // Examination fields (created together with the patient when toggled on).
      open_exam_now: true,
      exam_type: "form_086" as ExaminationType,
      job_title: "",
      routing: [] as DoctorSpecialty[],
      ticket_number: "",
    },
    validate: {
      full_name_raw: (v) =>
        v.trim().split(/\s+/).filter(Boolean).length >= 2
          ? null
          : t("validation.required"),
      birth_date: (v) => (v ? null : t("validation.required")),
      sex: (v) => (v ? null : t("validation.required")),
      phone_raw: (v) => {
        const c = canonicalPhoneUz(v);
        return /^\+998\d{9}$/.test(c) ? null : t("patients.validation.phone_format");
      },
      jshshir: (v) =>
        !v.trim() || /^\d{14}$/.test(v.replace(/\s+/g, ""))
          ? null
          : t("patients.validation.jshshir_format"),
      consent_signed: (v) => (v ? null : t("patients.validation.consent_required")),
      job_title: (v, all) =>
        all.open_exam_now && v.trim().length === 0
          ? t("validation.required")
          : null,
    },
  });

  // Mirror the patient's workplace into the examination's job_title so the
  // user only types it once. The exam's job_title field is read-only — to
  // change it the user edits the workplace field above.
  useEffect(() => {
    form.setFieldValue("job_title", form.values.workplace);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.workplace]);

  const mutation = useMutation({
    mutationFn: async (
      payload: PatientCreatePayload & {
        open_exam_now: boolean;
        exam_type: ExaminationType;
        job_title: string;
        routing: DoctorSpecialty[];
        ticket_number: string;
      },
    ) => {
      const { open_exam_now, exam_type, job_title, routing, ticket_number, ...patientPayload } = payload;
      const patient = await createPatient(patientPayload);
      let examId: string | null = null;
      if (open_exam_now) {
        const examPayload: ExaminationCreatePayload = {
          patient_id: patient.id,
          type: exam_type,
          job_title: job_title.trim(),
          routing: routing.length > 0 ? routing : null,
          ticket_number: ticket_number || null,
        };
        const exam = await createExamination(examPayload);
        examId = exam.id;
      }
      return { patient, examId };
    },
    onSuccess: ({ patient, examId }) => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      queryClient.invalidateQueries({ queryKey: ["patients-all"] });
      queryClient.invalidateQueries({ queryKey: ["examinations"] });
      notifications.show({
        color: "blue",
        title: t("patients.created_title"),
        message: `${patient.card_number}`,
      });
      if (examId) {
        navigate(`/examinations/${examId}`, { replace: true });
      } else {
        navigate(`/patients/${patient.id}`, { replace: true });
      }
    },
  });

  const onSubmit = form.onSubmit((values) => {
    if (!values.birth_date || !values.sex) return;
    const parts = values.full_name_raw.trim().split(/\s+/).filter(Boolean);
    const family_name = parts[0] ?? "";
    const given_name = parts[1] ?? "";
    const father_name = parts.slice(2).join(" ") || null;
    const passport = passportParts(values.passport_raw);
    const phone = canonicalPhoneUz(values.phone_raw);
    const payload = {
      family_name,
      given_name,
      father_name,
      birth_date: values.birth_date.toISOString().slice(0, 10),
      sex: values.sex,
      jshshir: values.jshshir.replace(/\s+/g, "") || null,
      passport_seriya: passport.seriya || null,
      passport_number: passport.number || null,
      address_region: values.address_region.trim() || null,
      address_district: values.address_district.trim() || null,
      address_full: values.address_full.trim() || null,
      phone_primary: phone,
      phone_secondary: null,
      email: values.email.trim() || null,
      workplace: values.workplace.trim() || null,
      position: values.position.trim() || null,
      consent_signed: values.consent_signed,
      open_exam_now: values.open_exam_now,
      exam_type: values.exam_type,
      job_title: values.job_title,
      routing: values.routing,
      ticket_number: values.ticket_number,
    };
    mutation.mutate(payload);
  });

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) {
      if (mutation.error.code === "conflict") return t("patients.errors.duplicate_jshshir");
      if (mutation.error.fields) {
        return Object.entries(mutation.error.fields)
          .map(([f, msgs]) => `${f}: ${msgs.join(", ")}`)
          .join(" | ");
      }
      return mutation.error.message;
    }
    return t("auth.network_error");
  })();

  return (
    <form onSubmit={onSubmit}>
      <Group justify="space-between" align="center" mb="xl">
        <Group gap="md">
          <UnstyledButton
            onClick={() => navigate(-1)}
            style={{
              color: "var(--mantine-color-gray-6)",
              padding: 8,
              borderRadius: 999,
            }}
          >
            <IconArrowLeft size={22} strokeWidth={2} />
          </UnstyledButton>
          <Title order={3} fw={700}>
            {t("patients.register")}
          </Title>
        </Group>
        <Group gap="lg">
          <UnstyledButton
            onClick={() => navigate("/patients")}
            style={{
              color: "var(--mantine-color-gray-6)",
              fontWeight: 500,
              fontSize: 14,
            }}
          >
            {t("common.cancel")}
          </UnstyledButton>
          <Button
            type="submit"
            radius="xl"
            size="md"
            loading={mutation.isPending}
            color="brand.7"
            px={32}
            style={{ boxShadow: "0 6px 16px rgba(37, 99, 235, 0.25)" }}
          >
            {t("common.save")}
          </Button>
        </Group>
      </Group>

      <Stack gap="lg" maw={1080} mx="auto">
        {errorMessage && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} radius="lg">
            {errorMessage}
          </Alert>
        )}

        <FormCard
          title={t("patients.section.identity")}
          icon={<IconUser size={18} strokeWidth={1.8} />}
        >
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <TextInput
              label={t("patients.fields.full_name_label")}
              placeholder={t("placeholder.full_name")}
              required
              {...form.getInputProps("full_name_raw")}
            />
            <TextInput
              label={t("patients.fields.passport_full")}
              placeholder="AA 1234567"
              value={formatPassportUz(form.values.passport_raw)}
              onChange={(e) => {
                form.setFieldValue("passport_raw", e.currentTarget.value);
              }}
              maxLength={10}
            />
            <TextInput
              label={t("patients.fields.phone_primary")}
              placeholder="+998 (90) 123-45-67"
              required
              value={formatPhoneUz(form.values.phone_raw || "+998")}
              onChange={(e) => {
                form.setFieldValue("phone_raw", e.currentTarget.value);
              }}
              error={form.errors.phone_raw}
              onFocus={(e) => {
                if (!form.values.phone_raw) {
                  form.setFieldValue("phone_raw", "+998");
                  const el = e.currentTarget;
                  setTimeout(() => {
                    if (el) el.setSelectionRange(el.value.length, el.value.length);
                  }, 0);
                }
              }}
            />
            <DateInput
              label={t("patients.fields.birth_date")}
              placeholder="mm/dd/yyyy"
              required
              valueFormat="DD.MM.YYYY"
              maxDate={new Date()}
              {...form.getInputProps("birth_date")}
            />
            <Box>
              <Text
                fw={700}
                size="xs"
                c="brand.9"
                tt="uppercase"
                mb={8}
                style={{ letterSpacing: "0.06em" }}
              >
                {t("patients.fields.sex")}
              </Text>
              <Group grow>
                <SexButton
                  label={t("patients.sex.male")}
                  selected={form.values.sex === "male"}
                  onClick={() => form.setFieldValue("sex", "male")}
                />
                <SexButton
                  label={t("patients.sex.female")}
                  selected={form.values.sex === "female"}
                  onClick={() => form.setFieldValue("sex", "female")}
                />
              </Group>
              {form.errors.sex && (
                <Text c="red" size="xs" fw={500} mt={4}>
                  {form.errors.sex}
                </Text>
              )}
            </Box>
            <TextInput
              label={t("patients.fields.jshshir")}
              placeholder="12345678901234"
              maxLength={14}
              value={form.values.jshshir}
              onChange={(e) => {
                const digits = e.currentTarget.value.replace(/\D/g, "").slice(0, 14);
                form.setFieldValue("jshshir", digits);
              }}
              error={form.errors.jshshir}
            />
            <TextInput
              label={t("patients.fields.email")}
              placeholder={t("placeholder.email")}
              type="email"
              {...form.getInputProps("email")}
            />
          </SimpleGrid>
        </FormCard>

        <FormCard
          title={t("patients.section.address")}
          icon={<IconMapPin size={18} strokeWidth={1.8} />}
        >
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <TextInput
              label={t("patients.fields.region")}
              placeholder={t("placeholder.address_region")}
              {...form.getInputProps("address_region")}
            />
            <TextInput
              label={t("patients.fields.district")}
              placeholder={t("placeholder.address_district")}
              {...form.getInputProps("address_district")}
            />
          </SimpleGrid>
          <TextInput
            mt="md"
            label={t("patients.fields.address_full")}
            placeholder={t("placeholder.address_full")}
            {...form.getInputProps("address_full")}
          />
        </FormCard>

        <FormCard
          title={t("patients.section.workplace")}
          icon={<IconBriefcase size={18} strokeWidth={1.8} />}
        >
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <TextInput
              label={t("patients.fields.workplace")}
              placeholder={t("placeholder.workplace")}
              {...form.getInputProps("workplace")}
            />
            <TextInput
              label={t("patients.fields.position")}
              placeholder={t("placeholder.position")}
              {...form.getInputProps("position")}
            />
          </SimpleGrid>
        </FormCard>

        <FormCard
          title={t("patients.section.exam")}
          icon={<IconBriefcase size={18} strokeWidth={1.8} />}
        >
          <Stack gap="md">
            <Switch
              label={t("patients.open_exam_now")}
              description={t("patients.open_exam_hint")}
              {...form.getInputProps("open_exam_now", { type: "checkbox" })}
            />
            {form.values.open_exam_now && (
              <Stack gap="md">
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <Select
                    label={t("examinations.fields.type")}
                    required
                    data={EXAM_TYPES.map((tp) => ({
                      value: tp,
                      label: t(`examinations.types.${tp}`),
                    }))}
                    {...form.getInputProps("exam_type")}
                  />
                  <TextInput
                    label={t("examinations.fields.ticket")}
                    placeholder="123"
                    {...form.getInputProps("ticket_number")}
                  />
                </SimpleGrid>
                <TextInput
                  label={t("examinations.fields.job_title")}
                  description={t("patients.job_title_synced")}
                  placeholder={t("examinations.job_title_placeholder")}
                  required
                  readOnly
                  {...form.getInputProps("job_title")}
                />
                <MultiSelect
                  label={t("examinations.fields.routing")}
                  description={
                    form.values.sex === "male"
                      ? t("examinations.routing_hint_male")
                      : t("examinations.routing_hint")
                  }
                  searchable
                  data={specialtiesForSex(form.values.sex).map((s) => ({
                    value: s,
                    label: t(`users.specialties.${s}`),
                  }))}
                  {...form.getInputProps("routing")}
                />
              </Stack>
            )}
          </Stack>
        </FormCard>

        <FormCard
          title={t("patients.section.consent")}
          icon={<IconFileCheck size={18} strokeWidth={1.8} />}
        >
          <Checkbox
            label={t("patients.fields.consent")}
            color="brand.7"
            {...form.getInputProps("consent_signed", { type: "checkbox" })}
          />
        </FormCard>
      </Stack>
    </form>
  );
}

function SexButton({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        height: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 8,
        border: selected
          ? "1.5px solid var(--mantine-color-brand-6)"
          : "1px solid var(--mantine-color-gray-2)",
        background: selected ? "var(--mantine-color-brand-0)" : "var(--mantine-color-gray-0)",
        color: selected
          ? "var(--mantine-color-brand-7)"
          : "var(--mantine-color-gray-7)",
        fontWeight: selected ? 700 : 500,
        fontSize: 14,
        transition: "all 120ms ease",
        boxShadow: selected ? "0 0 0 4px rgba(59, 130, 246, 0.10)" : "none",
      }}
    >
      {label}
    </UnstyledButton>
  );
}

function FormCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card padding="xl" radius="xl">
      <Group gap="sm" mb="lg" align="center">
        <ThemeIcon size={36} radius="xl" variant="light" color="brand">
          {icon}
        </ThemeIcon>
        <Title order={5} fw={700}>
          {title}
        </Title>
      </Group>
      {children}
    </Card>
  );
}
